// Which page does a floating bottom button bar belong to?
//
// On a small screen `cs-page-action-buttons` takes itself out of its own page and
// appends itself to one element that the whole app shares — `.split-pane-main-wrapper`
// in `app/app.html`. It has to: the bar is `position: fixed`, and a page that is being
// slid off-screen by a transform would otherwise drag the bar with it.
//
// The cost of that move is that the bar can no longer be found by looking inside the
// page it belongs to. And the shared element holds ONE BAR PER PAGE that is still alive
// underneath in the navigation stack — a bar is removed only when its page is destroyed,
// and they are appended oldest-page-first. So "the first bar in the shared element" is
// usually a LOWER page's bar, not the current page's.
//
// That mattered because `cs-content` reserves room at the bottom of its scrolled content
// for the bar, and it measures a bar that is not the active page's as zero pixels tall
// (correctly — an inactive page's bar is slid off-screen). Picking the wrong bar left the
// reserved space at zero for the whole life of the page, so the last card of the page sat
// behind the button bar and could not be scrolled into view (#4661).
//
// The repair is for a bar to remember which page it came from, before it moves, and for
// the lookup to match on that instead of taking whichever bar happens to be first.
//
// This module deliberately touches no DOM API and imports nothing from Angular, so the
// rule below can be unit tested in plain Node (the client unit tests run under mocha with
// no browser and no DOM library).

/**
 * The property the owning page is recorded under. A symbol so it cannot collide with
 * anything else that writes to the element. Module-private, so the only ways to set or
 * read it are the three functions below.
 */
const OWNER_PAGE_KEY: unique symbol = Symbol('csActionButtonsOwnerPage');

/**
 * Anything a bar's owning page can be recorded on. Deliberately just `object`, for two
 * reasons: a real `Element` is one, and so is the plain stand-in object a unit test uses
 * in place of an element. Declaring the symbol property here instead would make this a
 * type every element fails to match, because a type whose properties are all optional
 * only accepts values that share at least one of them.
 */
export type ActionButtonsOwnerCarrier = object;

/** The shape the two accessors below write and read through. Not part of the API. */
type OwnerRecord = { [OWNER_PAGE_KEY]?: unknown };

/**
 * Record which page this bar belongs to. Call this while the bar is still inside its own
 * page — the whole point is that the answer has to survive the move into the shared
 * element, where the DOM tree no longer says who the owner is.
 */
export function markActionButtonsOwnerPage(
  barElement: ActionButtonsOwnerCarrier,
  pageElement: unknown,
): void {
  (barElement as OwnerRecord)[OWNER_PAGE_KEY] = pageElement;
}

/** Read back the page a bar was recorded against, or undefined if it was never recorded. */
export function getActionButtonsOwnerPage(barElement: ActionButtonsOwnerCarrier): unknown {
  return (barElement as OwnerRecord)[OWNER_PAGE_KEY];
}

/**
 * Out of the bars parked in the shared element, pick the one that belongs to `pageElement`.
 *
 * Returns null when none of them does. That is the right answer rather than a fallback:
 * every other bar in there belongs to a different page, and reserving space for one of
 * those is exactly the bug this exists to stop.
 */
export function pickActionButtonsForPage<T extends ActionButtonsOwnerCarrier>(
  candidates: readonly T[],
  pageElement: unknown,
): T | null {
  // a bar that never recorded an owner belongs to nobody, so it must not be matched even
  // when the page we are asked about is itself unknown
  if (pageElement === undefined || pageElement === null) {
    return null;
  }
  return candidates.find(bar => getActionButtonsOwnerPage(bar) === pageElement) ?? null;
}
